/**
 * 목록 화면 검색 조건을 URL search params로만 관리한다.
 * - 필터 변경 시 navigate({ search })로 쿼리 갱신
 * - 상세 → 뒤로가기: 히스토리의 목록 URL 쿼리로 복원
 */
import { DEFAULT_PAGE_SIZE } from '@/shared/constants/pagination';

export type ListSearchPrimitive = string | number | boolean | undefined;
export type ListSearchValues = Record<string, ListSearchPrimitive>;

export const LIST_PAGINATION_DEFAULTS = {
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
} as const;

/** URL/unknown → defaults 타입에 맞춰 병합 */
export const mergeListSearch = <T extends ListSearchValues>(
  defaults: T,
  raw: Record<string, unknown> | null | undefined,
): T => {
  const next = { ...defaults };
  if (!raw) return next;

  (Object.keys(defaults) as (keyof T)[]).forEach((key) => {
    const fallback = defaults[key];
    const value = raw[key as string];
    if (value == null || value === '') {
      next[key] = fallback;
      return;
    }
    if (typeof fallback === 'number') {
      const n = typeof value === 'number' ? value : Number(value);
      next[key] = (Number.isFinite(n) ? n : fallback) as T[keyof T];
      return;
    }
    if (typeof fallback === 'boolean') {
      if (typeof value === 'boolean') {
        next[key] = value as T[keyof T];
      } else if (value === 'true' || value === '1') {
        next[key] = true as T[keyof T];
      } else if (value === 'false' || value === '0') {
        next[key] = false as T[keyof T];
      } else {
        next[key] = fallback;
      }
      return;
    }
    next[key] = String(value) as T[keyof T];
  });

  return next;
};

/** 기본값·빈 문자열은 URL에서 생략해 쿼리를 짧게 유지 */
export const toUrlSearch = <T extends ListSearchValues>(
  values: T,
  defaults: T,
): Record<string, string | number | boolean> => {
  const out: Record<string, string | number | boolean> = {};
  (Object.keys(values) as (keyof T)[]).forEach((key) => {
    const value = values[key];
    const fallback = defaults[key];
    if (value == null || value === '') return;
    if (value === fallback) return;
    if (typeof value === 'boolean' || typeof value === 'number') {
      out[key as string] = value;
      return;
    }
    out[key as string] = String(value);
  });
  return out;
};

export const hasMeaningfulListSearch = (
  raw: Record<string, unknown> | null | undefined,
  defaults: ListSearchValues,
): boolean => {
  if (!raw) return false;
  return Object.keys(defaults).some((key) => {
    const value = raw[key];
    if (value == null || value === '') return false;
    const fallback = defaults[key];
    if (typeof fallback === 'boolean') {
      return String(value) !== String(fallback);
    }
    if (typeof fallback === 'number') {
      return Number(value) !== fallback;
    }
    return String(value) !== String(fallback ?? '');
  });
};

/** 라우트 validateSearch용 — 쿼리를 그대로 통과 */
export const passThroughSearch = (
  search: Record<string, unknown>,
): Record<string, unknown> => search;
